import { useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Typography, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { Patient, ValidationErrors, validationErrors } from '../../views/Organisation/Cases/Common/patient';
import { getNhsInfo } from '../../utils/youConnectClient';
import { UnknownObject } from '../../react-app-env';
import { PatientNhsDetails } from '../../utils/patientNhsDetails';

interface Props {
  onPatientSelect: (patient: Patient) => void;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  validate: (field: string, value?: any) => UnknownObject;
  errors: ValidationErrors;
  isUnknownNhs: boolean;
}

const PatientNhsAutocomplete = ({ onPatientSelect, setErrors, validate, errors, isUnknownNhs }: Props) => {
  const [options, setOptions] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    const validation = validate('nhs_number', inputValue);

    if (validation.nhs_number) {
      setErrors((errors) => ({ ...errors, nhs_number: validation.nhs_number }));
      return;
    }

    setLoading(true);

    const timer = setTimeout(async () => {
      const data = await getNhsInfo(inputValue);

      if (!data) {
        setErrors((errors) => ({ ...errors, nhs_number: validationErrors.nhs_number_details }));
      } else {
        let patientNhsData: PatientNhsDetails = JSON.parse(data as string);

        const patient: Patient = {
          first_name: patientNhsData?.name[0]?.given[0] ?? '',
          last_name: patientNhsData.name[0].family as string,
          dob: moment(patientNhsData.birthDate, 'YYYY-MM-DD').toDate(),
          mrn: '',
          nhs_number: inputValue,
          gender: patientNhsData?.gender as string,
        };

        setOptions([patient]);
      }

      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  };

  return (
    <Autocomplete
      fullWidth
      disabled={isUnknownNhs}
      options={options}
      getOptionLabel={(option) => option.nhs_number}
      loading={loading}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label="NHS number"
          disabled={isUnknownNhs}
          required
          onChange={handleSearch}
          error={errors.nhs_number as boolean}
          helperText={errors.nhs_number}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : <SearchIcon sx={{ fill: (theme) => theme.palette.grey[600] }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      onChange={(_, value) => {
        if (value) {
          onPatientSelect(value);
          setErrors((errors) => ({ ...errors, nhs_number: false }));
        }
      }}
      renderOption={(props, option) => (
        <li
          {...props}
          style={{
            display: 'block',
            width: '100%',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography pb={1} fontSize={14} color="grey.600">
              {option.nhs_number}
            </Typography>
            <Typography variant="body1">
              {option.first_name} {option.last_name}
            </Typography>
            <Typography fontSize={14} color="grey.600">
              {moment(option.dob).format('DD/MM/YYYY')} • {capitalize(option.gender)}
            </Typography>
          </Box>
        </li>
      )}
    />
  );
};

const capitalize = (s: string) => {
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default PatientNhsAutocomplete;
